import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Tag, Save, X, AlertTriangle, Calendar, Trash2, Scan } from 'lucide-react';
import CameraScanner from './CameraScanner';

const CylinderEditor = ({ cylinders = [], setCylinders, customers = [], warehouses = [], items = [] }) => {
  const [cylEdits, setCylEdits] = useState({});
  const [cylSaved, setCylSaved] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [scanMode, setScanMode] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (scanMode && searchInputRef.current) searchInputRef.current.focus();
  }, [scanMode]);

  // 각각 독립된 필터
  const [searchId, setSearchId]         = useState('');  // 용기번호 검색
  const [filterItem, setFilterItem]     = useState('');  // 품목
  const [filterLocation, setFilterLocation] = useState(''); // 위치
  const [filterStatus, setFilterStatus] = useState('');  // 상태
  const [filterExpiry, setFilterExpiry] = useState('');  // 검사기한

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const allItemTypes = useMemo(() => {
    const fromSettings = items.map(i => i.name || i);
    const fromCylinders = cylinders.map(c => c.item).filter(Boolean);
    return [...new Set([...fromSettings, ...fromCylinders])].sort();
  }, [items, cylinders]);

  // 위치 목록: "창고 · 제1창고" 형태로 통합
  const allLocationOptions = useMemo(() => {
    const wh = warehouses.map(w => ({ value: `warehouse::${w}`, label: `[창고] ${w}` }));
    const cu = customers.map(c => ({ value: `customer::${c.name}`, label: `[거래처] ${c.name}` }));
    return [...wh, ...cu];
  }, [warehouses, customers]);

  const filteredCylinders = useMemo(() => {
    return cylinders.filter(c => {
      if (searchId.trim()) {
        if (!(c.id || '').toLowerCase().includes(searchId.trim().toLowerCase())) return false;
      }
      if (filterItem && c.item !== filterItem) return false;
      if (filterLocation) {
        const [locType, locName] = filterLocation.split('::');
        if (c.locType !== locType || c.location !== locName) return false;
      }
      if (filterStatus && c.status !== filterStatus) return false;
      if (filterExpiry) {
        const expiry = c.inspectionExpiry ? new Date(c.inspectionExpiry) : null;
        if (filterExpiry === 'expired') {
          if (!expiry || expiry >= today) return false;
        } else if (filterExpiry === 'soon30') {
          if (!expiry) return false;
          const diff = (expiry - today) / (1000 * 60 * 60 * 24);
          if (diff < 0 || diff > 30) return false;
        } else if (filterExpiry === 'soon90') {
          if (!expiry) return false;
          const diff = (expiry - today) / (1000 * 60 * 60 * 24);
          if (diff < 0 || diff > 90) return false;
        } else if (filterExpiry === 'none') {
          if (c.inspectionExpiry) return false;
        }
      }
      return true;
    });
  }, [cylinders, searchId, filterItem, filterLocation, filterStatus, filterExpiry, today]);

  const activeCount = [searchId, filterItem, filterLocation, filterStatus, filterExpiry].filter(Boolean).length;

  const resetFilters = () => {
    setSearchId('');
    setFilterItem('');
    setFilterLocation('');
    setFilterStatus('');
    setFilterExpiry('');
  };

  const handleCylEdit = (originalId, field, value) => {
    setCylEdits(prev => ({
      ...prev,
      [originalId]: { ...(prev[originalId] || {}), [field]: value },
    }));
  };

  const handleSave = () => {
    if (!setCylinders) return;
    setCylinders(prev => prev.map(c => {
      const edits = cylEdits[c.id];
      if (!edits) return c;
      let locUpdate = {};
      if (edits.locationKey !== undefined) {
        const [locType, locName] = edits.locationKey.split('::');
        locUpdate = { locType, location: locName };
      }
      return {
        ...c,
        ...(edits.newId !== undefined && edits.newId.trim() !== '' ? { id: edits.newId.trim() } : {}),
        ...(edits.item !== undefined ? { item: edits.item } : {}),
        ...(edits.status !== undefined ? { status: edits.status } : {}),
        ...(edits.inspectionExpiry !== undefined ? { inspectionExpiry: edits.inspectionExpiry } : {}),
        ...locUpdate,
      };
    }));
    setCylEdits({});
    setCylSaved(true);
    setTimeout(() => setCylSaved(false), 2000);
  };

  const handleDelete = (cylId) => {
    setCylinders(prev => prev.filter(c => c.id !== cylId));
    setConfirmDeleteId(null);
    const newEdits = { ...cylEdits };
    delete newEdits[cylId];
    setCylEdits(newEdits);
  };

  const getExpiryBadge = (inspectionExpiry) => {
    if (!inspectionExpiry) return null;
    const expiry = new Date(inspectionExpiry);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (diff < 0)  return { label: '만료',    bg: '#fee2e2', color: '#dc2626' };
    if (diff <= 30) return { label: `D-${diff}`, bg: '#fef3c7', color: '#d97706' };
    if (diff <= 90) return { label: `D-${diff}`, bg: '#fffbeb', color: '#92400e' };
    return null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={boxStyle}>

        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Tag color="#7c3aed" />
          <h3 style={{ margin: 0 }}>용기 번호 및 검사기한 관리</h3>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
            전체 {cylinders.length}개 중 <strong>{filteredCylinders.length}개</strong> 표시
          </span>
        </div>

        {/* 필터 영역 */}
        <div style={filterBoxStyle}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>

            {/* 용기번호 + 바코드 스캔 */}
            <div style={filterGroupStyle}>
              <label style={labelStyle}>용기번호</label>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={scanMode ? "스캔 대기 중..." : "번호 입력..."}
                    value={searchId}
                    onChange={e => setSearchId(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && scanMode) e.preventDefault(); }}
                    style={{
                      ...filterInputStyle,
                      paddingRight: searchId ? 28 : 10,
                      borderColor: scanMode ? '#7c3aed' : '#d1d5db',
                      background: scanMode ? '#faf5ff' : 'white',
                      fontFamily: scanMode ? 'monospace' : undefined,
                    }}
                  />
                  {searchId && (
                    <button onClick={() => setSearchId('')} style={clearBtnStyle}>
                      <X size={13} />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  title={scanMode ? "스캔 모드 끄기" : "바코드 스캔 모드"}
                  onClick={() => {
                    setScanMode(prev => !prev);
                    setSearchId('');
                  }}
                  style={{
                    padding: '6px 8px', borderRadius: 8, border: `1.5px solid ${scanMode ? '#7c3aed' : '#d1d5db'}`,
                    background: scanMode ? '#ede9fe' : 'white', cursor: 'pointer',
                    color: scanMode ? '#7c3aed' : '#6b7280', display: 'flex', alignItems: 'center', flexShrink: 0,
                  }}>
                  <Scan size={15} />
                </button>
                <CameraScanner
                  color="#7c3aed"
                  onScan={(code) => { setSearchId(code); setScanMode(true); searchInputRef.current?.focus(); }}
                />
              </div>
              {scanMode && (
                <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, marginTop: 3 }}>
                  바코드 스캔 후 Enter 또는 카메라로 스캔
                </span>
              )}
            </div>

            {/* 품목 */}
            <div style={filterGroupStyle}>
              <label style={labelStyle}>품목</label>
              <select value={filterItem} onChange={e => setFilterItem(e.target.value)} style={filterSelectStyle}>
                <option value="">전체</option>
                {allItemTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* 위치 */}
            <div style={{ ...filterGroupStyle, minWidth: 180 }}>
              <label style={labelStyle}>위치</label>
              <select value={filterLocation} onChange={e => setFilterLocation(e.target.value)} style={filterSelectStyle}>
                <option value="">전체</option>
                {allLocationOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* 상태 */}
            <div style={filterGroupStyle}>
              <label style={labelStyle}>상태</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={filterSelectStyle}>
                <option value="">전체</option>
                <option value="실병">실병</option>
                <option value="공병">공병</option>
              </select>
            </div>

            {/* 검사기한 */}
            <div style={{ ...filterGroupStyle, minWidth: 150 }}>
              <label style={labelStyle}>검사기한</label>
              <select
                value={filterExpiry}
                onChange={e => setFilterExpiry(e.target.value)}
                style={{ ...filterSelectStyle, borderColor: filterExpiry ? '#f59e0b' : '#d1d5db' }}
              >
                <option value="">전체</option>
                <option value="expired">만료됨</option>
                <option value="soon30">30일 이내 만료</option>
                <option value="soon90">90일 이내 만료</option>
                <option value="none">기한 미입력</option>
              </select>
            </div>

            {/* 초기화 */}
            {activeCount > 0 && (
              <button onClick={resetFilters} style={resetBtnStyle}>
                <X size={13} /> 초기화 ({activeCount})
              </button>
            )}
          </div>
        </div>

        {/* 테이블 */}
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f5f3ff' }}>
                <th style={thStyle}>용기번호</th>
                <th style={thStyle}>품목</th>
                <th style={thStyle}>상태</th>
                <th style={thStyle}>위치 (수정 가능)</th>
                <th style={thStyle}>검사기한</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {cylinders.length === 0 ? (
                <tr><td colSpan={6} style={{ ...tdStyle, color: '#aaa', textAlign: 'center', padding: '32px 0' }}>
                  등록된 용기가 없습니다. 이월재고 탭에서 기초 재고를 먼저 저장하세요.
                </td></tr>
              ) : filteredCylinders.length === 0 ? (
                <tr><td colSpan={6} style={{ ...tdStyle, color: '#aaa', textAlign: 'center', padding: '32px 0' }}>
                  조건에 맞는 용기가 없습니다.
                </td></tr>
              ) : filteredCylinders.map(c => {
                const edits = cylEdits[c.id] || {};
                const isEdited = !!cylEdits[c.id];
                const currentLocKey = edits.locationKey !== undefined
                  ? edits.locationKey
                  : `${c.locType}::${c.location}`;
                const currentExpiry = edits.inspectionExpiry !== undefined ? edits.inspectionExpiry : (c.inspectionExpiry || '');
                const expBadge = getExpiryBadge(currentExpiry);
                const currentItem   = edits.item   !== undefined ? edits.item   : c.item;
                const currentStatus = edits.status  !== undefined ? edits.status : c.status;
                const isDeleting = confirmDeleteId === c.id;
                return (
                  <React.Fragment key={c.id}>
                  <tr style={isDeleting ? { background: '#fef2f2' } : isEdited ? { background: '#fefce8' } : {}}>
                    <td style={tdStyle}>
                      <input
                        style={{ ...cylIdInput, borderColor: isEdited ? '#f59e0b' : '#d1d5db' }}
                        value={edits.newId !== undefined ? edits.newId : c.id}
                        onChange={e => handleCylEdit(c.id, 'newId', e.target.value)}
                        placeholder="용기번호"
                      />
                    </td>
                    <td style={tdStyle}>
                      <select
                        style={{ ...inlineSelectStyle, borderColor: isEdited && edits.item !== undefined ? '#f59e0b' : '#d1d5db' }}
                        value={currentItem}
                        onChange={e => handleCylEdit(c.id, 'item', e.target.value)}
                      >
                        {allItemTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        {!allItemTypes.includes(currentItem) && <option value={currentItem}>{currentItem}</option>}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <select
                        style={{ ...inlineSelectStyle, minWidth: 70, borderColor: isEdited && edits.status !== undefined ? '#f59e0b' : '#d1d5db' }}
                        value={currentStatus}
                        onChange={e => handleCylEdit(c.id, 'status', e.target.value)}
                      >
                        <option value="실병">실병</option>
                        <option value="공병">공병</option>
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <select
                        style={{ ...inlineSelectStyle, minWidth: 160, borderColor: isEdited && edits.locationKey !== undefined ? '#f59e0b' : '#d1d5db' }}
                        value={currentLocKey}
                        onChange={e => handleCylEdit(c.id, 'locationKey', e.target.value)}
                      >
                        {allLocationOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        {!allLocationOptions.find(o => o.value === currentLocKey) && (
                          <option value={currentLocKey}>{currentLocKey.replace('::', ' · ')}</option>
                        )}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="date"
                          style={{ ...cylDateInput, borderColor: isEdited && edits.inspectionExpiry !== undefined ? '#f59e0b' : '#d1d5db' }}
                          value={currentExpiry}
                          onChange={e => handleCylEdit(c.id, 'inspectionExpiry', e.target.value)}
                        />
                        {expBadge && (
                          <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: expBadge.bg, color: expBadge.color, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3 }}>
                            {expBadge.label === '만료' ? <AlertTriangle size={10} /> : <Calendar size={10} />}
                            {expBadge.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button
                        onClick={() => setConfirmDeleteId(isDeleting ? null : c.id)}
                        title="용기 삭제"
                        style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: '#DC2626', display: 'flex', alignItems: 'center' }}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                  {isDeleting && (
                    <tr style={{ background: '#fef2f2' }}>
                      <td colSpan={6} style={{ padding: '8px 12px', borderBottom: '1px solid #fecaca' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, color: '#DC2626', fontWeight: 600 }}>
                            [{c.id}] 용기를 목록에서 삭제하시겠습니까? (복구 불가)
                          </span>
                          <button onClick={() => handleDelete(c.id)}
                            style={{ padding: '5px 14px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                            삭제 확인
                          </button>
                          <button onClick={() => setConfirmDeleteId(null)}
                            style={{ padding: '5px 12px', background: '#fff', color: '#666', border: '1px solid #d3d1c7', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                            취소
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 저장 버튼 */}
        {cylinders.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              {Object.keys(cylEdits).length > 0 && `${Object.keys(cylEdits).length}건 수정됨`}
            </span>
            <button
              onClick={handleSave}
              disabled={Object.keys(cylEdits).length === 0}
              style={{
                ...cylSaveBtn,
                background: cylSaved ? '#059669' : Object.keys(cylEdits).length === 0 ? '#d1d5db' : '#7c3aed',
                cursor: Object.keys(cylEdits).length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              <Save size={16} />
              {cylSaved ? '저장 완료!' : `용기 정보 저장${Object.keys(cylEdits).length > 0 ? ` (${Object.keys(cylEdits).length}건)` : ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const boxStyle         = { background: 'white', padding: 25, borderRadius: 16, border: '1px solid #d3d1c7' };
const filterBoxStyle   = { background: '#f8f7ff', border: '1px solid #e9d5ff', borderRadius: 12, padding: '14px 16px', marginBottom: 20 };
const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 };
const labelStyle       = { fontSize: 11, fontWeight: 700, color: '#7c3aed', letterSpacing: '0.03em' };
const filterInputStyle = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', width: 140, boxSizing: 'border-box' };
const filterSelectStyle = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white', cursor: 'pointer', width: '100%' };
const clearBtnStyle    = { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' };
const resetBtnStyle    = { display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px', background: '#ede9fe', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end' };
const tableStyle       = { width: '100%', borderCollapse: 'collapse' };
const thStyle          = { padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 700 };
const tdStyle          = { padding: '10px 8px', borderBottom: '1px solid #eee', fontSize: 14 };
const inlineSelectStyle = { padding: '5px 6px', border: '1px solid', borderRadius: 4, fontSize: 13, background: 'white', cursor: 'pointer' };
const cylIdInput       = { width: '140px', padding: '5px 8px', border: '1px solid', borderRadius: 4, fontSize: 13 };
const cylDateInput     = { padding: '5px 8px', border: '1px solid', borderRadius: 4, fontSize: 13 };
const cylSaveBtn       = { padding: '10px 22px', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, transition: 'background 0.2s' };

export default CylinderEditor;
